import React from "react";
import { Box, Typography, Divider } from "@mui/material";

/**
 * A consistent page header component with title and optional action button
 *
 * @param {Object} props - Component props
 * @param {String} props.title - The page title
 * @param {String} props.subtitle - Optional subtitle text
 * @param {Node} props.startAction - Optional component to display before the title
 * @param {Node} props.endAction - Optional component to display at the end (usually a button)
 * @param {Boolean} props.divider - Whether to show a divider after the header
 * @param {Object} props.sx - Additional styling
 */
const PageHeader = ({
  title,
  subtitle,
  startAction,
  endAction,
  divider = true,
  sx = {},
}) => {
  return (
    <Box sx={{ width: "100%", mb: divider ? 3 : 2, ...sx }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "stretch", md: "center" },
          gap: { xs: 2, sm: 2, md: 0 },
          mb: subtitle ? 0.5 : divider ? 2 : 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {startAction && <Box sx={{ mr: 2 }}>{startAction}</Box>}
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>

        {subtitle && (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mt: -1,
              visibility: { xs: "visible", sm: "visible", md: "hidden" },
            }}
          >
            {subtitle}
          </Typography>
        )}

        {endAction && (
          <Box sx={{ width: { xs: "100%", sm: "100%", md: "auto" } }}>
            {endAction}
          </Box>
        )}
      </Box>

      {subtitle && (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mb: { xs: 0, sm: 0, md: divider ? 2 : 0 },
            visibility: { xs: "hidden", sm: "hidden", md: "visible" },
          }}
        >
          {subtitle}
        </Typography>
      )}

      {divider && <Divider sx={{ mb: 2 }} />}
    </Box>
  );
};

export default PageHeader;
